import { Injectable } from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {AppConfig} from '../../app/app.config';
import {FinaliseJackingV2Request, JackingFormInstanceEditor, JackingFormInstanceSummary} from '../../models/jacking-v2/jacking.models';
import {DateParserService} from '../daily-log/daily-log-time/date-parser-service';
import {TimeZoneService} from '../time-zone/time-zone.service';
import { cloneDeep } from 'lodash';
import {PtLuxonDate} from '../../models/projects/date.models';
import {Observable} from 'rxjs/Observable';
import {JackingActivityEditor} from '../../models/projects/jacking-activity.models';
import {ActionType, ErrorType, PageValidationType, ValidationResult} from '../../models/projects/validation.models';

@Injectable()
export class JackingV2Service {
  private baseUrl = `${AppConfig.settings.apiUrl}api/jacking-v2`;

  constructor(private http: HttpClient,
              private dateParserService: DateParserService,
              private timeZoneService: TimeZoneService,
  ) {
  }

  async getSummariesForDate(dailyLogDateUnixMs: number): Promise<JackingFormInstanceSummary[]> {
    return this.http
      .get<JackingFormInstanceSummary[]>(`${this.baseUrl}/summary/${dailyLogDateUnixMs}/all`)
      .toPromise()
      .then(async (data: JackingFormInstanceSummary[]) => {
        data = await this.dateParserService.deserializeDates(data, true, this.timeZoneService);
        return data;
      });
  }

  async getFormInstance(dailyLogDateUnixMs: number, jackingInstanceId: number): Promise<JackingFormInstanceEditor>  {
    return this.http
      .get<JackingFormInstanceEditor>(`${this.baseUrl}/${dailyLogDateUnixMs}/${jackingInstanceId}`)
      .toPromise()
      .then(async (editor: JackingFormInstanceEditor) => {
        editor = await this.dateParserService.deserializeDates(editor, true, this.timeZoneService);
        return editor;
      });
  }

  async saveFormInstance(dailyLogDateUnixMs: number, editor: JackingFormInstanceEditor): Promise<JackingFormInstanceEditor> {
    const url = `${this.baseUrl}/${dailyLogDateUnixMs}`;
    let payload = cloneDeep(editor);

    // Remove offsets from any Luxon DateTimes in the payload
    payload = this.dateParserService.removeOffsetFromLuxonDates(payload);

    // NOW stringify and post
    const saved = await this.http.post<JackingFormInstanceEditor>(url, payload).toPromise();

    // re-hydrate dates into Luxon DateTime objects
    return await this.dateParserService.deserializeDates(saved, true, this.timeZoneService);
  }

  public downloadJackingPdf(
    dailyLogDate: PtLuxonDate,
    instanceId: number,
    formTypeId: number,
    incrementVersion: boolean,
    isPreview: boolean
  ): Observable<HttpResponse<Blob>> {
    const dateLong = dailyLogDate.dateTime.toMillis();

    const url =
      `${this.baseUrl}/reports/pdf/${dateLong}/${instanceId}` +
      `?download=true` +
      `&incrementVersion=${incrementVersion}` +
      `&isPreview=${isPreview}` +
      `&formTypeId=${formTypeId}`;

    return this.http.get(url, { responseType: 'blob', observe: 'response' });
  }

  async completeFormInstance(editor: JackingFormInstanceEditor): Promise<void> {
    const url = `${this.baseUrl}/${editor.id}/complete`;
    let payload = cloneDeep(editor);

    // Remove offsets from any Luxon DateTimes in the payload
    payload = this.dateParserService.removeOffsetFromLuxonDates(payload);

    return this.http.post<void>(url, payload).toPromise();
  }

  cancelFormInstance(formInstanceId: number, req: FinaliseJackingV2Request): Promise<void> {
    const url = `${this.baseUrl}/${formInstanceId}/cancel`;
    const payload = cloneDeep(req);

    if (payload.instanceEditor) {
      payload.instanceEditor = this.dateParserService.removeOffsetFromLuxonDates(payload.instanceEditor);
    }

    return this.http.post<void>(url, payload).toPromise();
  }

  public isFinalisedFormInstanceStatus(summary: Pick<JackingFormInstanceSummary, 'statusId' | 'activityStatusName'>): boolean {
    const statusId = String(summary?.statusId || '').toLowerCase();
    const activityStatusName = String(summary?.activityStatusName || '').toLowerCase();

    return statusId === 'completed'
      || statusId === 'confirmed'
      || statusId === 'cancelled'
      || activityStatusName === 'cancelled';
  }

  public validateFormInstances(existingResult: ValidationResult, summaries: JackingFormInstanceSummary[]): ValidationResult {
    const value = summaries.every(e => this.isFinalisedFormInstanceStatus(e));

    if (!value) {
      existingResult.addError(
        'There are outstanding Jacking Activities which need to be completed',
        ErrorType.Warning,
        ActionType.Unconfirmed,
        null,
        PageValidationType.JackingActivity
      );
    }

    return existingResult;
  }
}



// WEBPACK FOOTER //
// ./src/services/jacking-v2/jacking-v2.service.ts